import * as React from 'react';
import clsx from 'clsx';
import SignatureCanvas, { SignaturePadTouchApi } from 'react-signature-canvas';
import { AlertTriangle, Trash2 } from 'lucide-react';
import { Button, Icon, Typography } from '@/components/common';
import {
	PdfFormField,
	PdfExtractPage,
	PdfImageMatch,
} from '@/lib/matchPdfFormFields';
import {
	useClientSignSession,
	ClientSignSession,
} from './hooks/useClientSignSession';
import {
	ClientSignPdfCanvas,
	ClientSignPdfCanvasHandle,
} from './ClientSignPdfCanvas';
import {
	ClientSignHtmlView,
	ClientSignHtmlViewHandle,
} from './ClientSignHtmlView';
import Icons from '@/const/icons';
import { DocumentSubmittedSuccessMessage } from './DocumentSubmittedSuccessMessage';
import { ClientSignFieldProgressNav } from './ClientSignFieldProgressNav';
import { useClientSignOrderedFields } from './hooks/useClientSignOrderedFields';

type ViewerMode = 'pdf' | 'html';

export interface ClientSignMobileViewProps {
	documentUrl: string;
	extractedFields?: PdfFormField[];
	textPages?: PdfExtractPage[];
	documentImages?: PdfImageMatch[];
	onClose: () => void;
	preview?: boolean;
	session: ClientSignSession;
	submitted?: boolean;
	documentName?: string;
	wasPreviouslyDeclined?: boolean;
}

const ClientSignMobileMismatch = ({
	fieldIds,
	fields,
	settingsByFieldId,
}: {
	fieldIds: string[];
	fields: ReturnType<typeof useClientSignSession>['fields'];
	settingsByFieldId: Record<string, string>;
}) => (
	<div className="rounded-2xl border border-[#BB4D00] bg-[#FFF6ED] px-3 py-2 mb-3">
		<div className="flex items-center gap-2">
			<AlertTriangle className="size-4 shrink-0 text-[#BB4D00]" aria-hidden />
			<Typography type="semibold-14" className="!text-[#BB4D00] text-left">
				{fieldIds.length === 1
					? 'A field does not match the recorded data.'
					: 'Some fields do not match the recorded data.'}
			</Typography>
		</div>
		<div className="mt-3 flex flex-col gap-4 text-left">
			{fieldIds.map((fieldId) => (
				<div key={fieldId}>
					<Typography type="semibold-14">
						{fields[fieldId]?.label} <span className="text-main-red">*</span>
					</Typography>
					<div className="mt-2 rounded-lg bg-[#F6F8E3] px-3 py-2">
						<Typography type="bold-12" className="uppercase tracking-wide">
							In your settings
						</Typography>
						<Typography type="regular-12">
							{settingsByFieldId[fieldId] || '—'}
						</Typography>
					</div>
				</div>
			))}
		</div>
	</div>
);

/**
 * signature_pad skips touchstart when two fingers are already down, then
 * touchmove still paints. That leaves ink on the canvas without onBegin, so
 * Confirm stays disabled and "Sign here" stays up. Draw with the first finger
 * only.
 */
const copyCanvas = (source: HTMLCanvasElement): HTMLCanvasElement => {
	const copy = document.createElement('canvas');
	copy.width = source.width;
	copy.height = source.height;
	copy.getContext('2d')?.drawImage(source, 0, 0);
	return copy;
};

/** Ink is opaque. The pad background is transparent, so a cleared canvas has alpha 0. */
const canvasHasInk = (canvas: HTMLCanvasElement): boolean => {
	const context = canvas.getContext('2d');
	if (!context || canvas.width === 0 || canvas.height === 0) return false;
	const { data } = context.getImageData(0, 0, canvas.width, canvas.height);
	for (let index = 3; index < data.length; index += 4) {
		if (data[index] !== 0) return true;
	}
	return false;
};

const paintSignatureImage = (
	canvas: HTMLCanvasElement,
	source: HTMLCanvasElement
) => {
	const context = canvas.getContext('2d');
	if (!context || source.width === 0 || source.height === 0) return;
	const scale = Math.min(
		canvas.width / source.width,
		canvas.height / source.height
	);
	const width = source.width * scale;
	const height = source.height * scale;
	const x = (canvas.width - width) / 2;
	const y = (canvas.height - height) / 2;
	context.clearRect(0, 0, canvas.width, canvas.height);
	context.drawImage(
		source,
		0,
		0,
		source.width,
		source.height,
		x,
		y,
		width,
		height
	);
};

const bindSingleTouchDrawing = (
	canvas: HTMLCanvasElement,
	pad: SignaturePadTouchApi,
	onInkChange: (empty: boolean) => void
): (() => void) => {
	canvas.removeEventListener('touchstart', pad._handleTouchStart);
	canvas.removeEventListener('touchmove', pad._handleTouchMove);
	canvas.removeEventListener('touchend', pad._handleTouchEnd);

	let active = false;

	const begin = (touch: Touch) => {
		active = true;
		pad._strokeBegin(touch);
	};

	const start = (event: TouchEvent) => {
		if (event.cancelable) event.preventDefault();
		if (active) return;
		const touch = event.targetTouches[0] ?? event.changedTouches[0];
		if (!touch) return;
		begin(touch);
	};

	const move = (event: TouchEvent) => {
		if (event.cancelable) event.preventDefault();
		const touch = event.targetTouches[0];
		if (!touch) return;
		if (!active) {
			begin(touch);
			return;
		}
		pad._strokeMoveUpdate(touch);
	};

	const end = (event: TouchEvent) => {
		if (event.targetTouches.length > 0) {
			if (event.cancelable) event.preventDefault();
			return;
		}
		if (!active) return;
		if (event.cancelable) event.preventDefault();
		active = false;
		pad._strokeEnd(event);
		onInkChange(pad.isEmpty());
	};

	const options = { passive: false } as const;
	canvas.addEventListener('touchstart', start, options);
	canvas.addEventListener('touchmove', move, options);
	canvas.addEventListener('touchend', end, options);
	canvas.addEventListener('touchcancel', end, options);

	return () => {
		canvas.removeEventListener('touchstart', start);
		canvas.removeEventListener('touchmove', move);
		canvas.removeEventListener('touchend', end);
		canvas.removeEventListener('touchcancel', end);
	};
};

const MobileSignatureScreen = ({
	onApply,
}: {
	onClose: () => void;
	onApply: (dataUrl: string) => void;
}) => {
	const padRef = React.useRef<SignatureCanvas | null>(null);
	const padContainerRef = React.useRef<HTMLDivElement>(null);
	const padSizeRef = React.useRef({ width: 0, height: 0 });
	const restoreRef = React.useRef<{
		image: HTMLCanvasElement;
		width: number;
		height: number;
	} | null>(null);
	const [padSize, setPadSize] = React.useState({ width: 0, height: 0 });
	const [isEmpty, setIsEmpty] = React.useState(true);
	const [error, setError] = React.useState<string | null>(null);

	const padReady = padSize.width > 0 && padSize.height > 0;

	React.useLayoutEffect(() => {
		const container = padContainerRef.current;
		if (!container) return undefined;
		const updateSize = () => {
			const width = container.clientWidth;
			const height = container.clientHeight;
			const previous = padSizeRef.current;
			const pad = padRef.current;
			const canvas = pad?.getCanvas();
			// Snapshot before React writes the new canvas size. That write wipes
			// the bitmap and leaves signature_pad thinking the stroke is still there.
			if (
				pad &&
				canvas &&
				!pad.isEmpty() &&
				previous.width > 0 &&
				previous.height > 0 &&
				canvas.width === previous.width &&
				canvas.height === previous.height &&
				(previous.width !== width || previous.height !== height)
			) {
				restoreRef.current = {
					image: copyCanvas(canvas),
					width: canvas.width,
					height: canvas.height,
				};
			}
			padSizeRef.current = { width, height };
			setPadSize({ width, height });
		};
		updateSize();
		const observer = new ResizeObserver(updateSize);
		observer.observe(container);
		return () => observer.disconnect();
	}, []);

	React.useLayoutEffect(() => {
		if (!padReady) return;
		const pad = padRef.current;
		if (!pad) return;
		const canvas = pad.getCanvas();
		const pending = restoreRef.current;
		if (
			pending &&
			(canvas.width !== pending.width || canvas.height !== pending.height)
		) {
			paintSignatureImage(canvas, pending.image);
			restoreRef.current = null;
		}
		if (canvasHasInk(canvas)) {
			setIsEmpty(false);
			return;
		}
		if (!pad.isEmpty()) pad.clear();
		setIsEmpty(true);
	}, [padReady, padSize.width, padSize.height]);

	React.useEffect(() => {
		if (!padReady) return undefined;
		const signature = padRef.current;
		if (!signature) return undefined;
		return bindSingleTouchDrawing(
			signature.getCanvas(),
			signature.getSignaturePad(),
			setIsEmpty
		);
	}, [padReady]);

	return (
		<div className="flex h-full min-h-0 flex-col bg-white px-4 pb-4 pt-5 text-left">
			<Typography type="semibold-16">Draw your signature</Typography>
			<Typography type="regular-12" className="mt-1 text-gray-35">
				Use your finger to sign below
			</Typography>
			<div
				ref={padContainerRef}
				className="relative mt-4 min-h-[280px] flex-1 overflow-hidden rounded-2xl border border-[#E6E6E6] bg-[#FAFAFA]"
			>
				<span className="pointer-events-none absolute inset-x-8 bottom-[18%] border-b border-dashed border-[#D5D5D5]" />
				{isEmpty && (
					<span className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-[#C4C4C4]">
						Sign here…
					</span>
				)}
				{padSize.width > 0 && padSize.height > 0 && (
					<SignatureCanvas
						ref={padRef}
						penColor="#0D0616"
						clearOnResize={false}
						onBegin={() => {
							setError(null);
							setIsEmpty(false);
						}}
						canvasProps={{
							className: 'relative z-[1] block bg-transparent touch-none',
							width: padSize.width,
							height: padSize.height,
							'aria-label': 'Signature drawing area',
						}}
					/>
				)}
			</div>
			{error && (
				<Typography type="regular-14" className="mt-2 text-main-red">
					{error}
				</Typography>
			)}
			<div className="flex items-center gap-3 pt-4">
				<button
					type="button"
					disabled={isEmpty}
					onClick={() => {
						restoreRef.current = null;
						padRef.current?.clear();
						setIsEmpty(true);
						setError(null);
					}}
					className="inline-flex items-center gap-2 rounded-full border border-[#E4E4E4] bg-white px-5 py-3 text-sm font-semibold text-black disabled:opacity-40"
				>
					<Trash2 className="size-4" />
					Clear
				</button>
				<button
					type="button"
					disabled={isEmpty}
					onClick={() => {
						const pad = padRef.current;
						const canvas = pad?.getCanvas();
						if (!pad || pad.isEmpty() || !canvas || !canvasHasInk(canvas)) {
							pad?.clear();
							restoreRef.current = null;
							setIsEmpty(true);
							setError('Please draw your signature before confirming it.');
							return;
						}
						onApply(pad.toDataURL('image/png'));
					}}
					className="flex-1 rounded-full bg-primary px-5 py-3 text-sm font-semibold text-white disabled:bg-[#D4C4F5]"
				>
					Confirm Signature
				</button>
			</div>
		</div>
	);
};

export const ClientSignMobileView: React.FC<ClientSignMobileViewProps> = ({
	documentUrl,
	extractedFields,
	textPages = [],
	documentImages = [],
	onClose,
	preview = false,
	session,
	submitted = false,
	documentName,
	wasPreviouslyDeclined = false,
}) => {
	const [viewer, setViewer] = React.useState<ViewerMode>('html');
	const [pdfViewerActivated, setPdfViewerActivated] = React.useState(false);
	const [contentWidth, setContentWidth] = React.useState(320);
	const [keyboardOpen, setKeyboardOpen] = React.useState(false);
	const canvasRef = React.useRef<ClientSignPdfCanvasHandle>(null);
	const htmlRef = React.useRef<ClientSignHtmlViewHandle>(null);
	const viewerSlotRef = React.useRef<HTMLDivElement>(null);
	const cycleFieldIdRef = React.useRef<string | null>(null);
	const htmlAvailable = textPages.length > 0;

	React.useEffect(() => {
		const isTextEntry = (node: EventTarget | null): boolean => {
			if (!(node instanceof HTMLElement)) return false;
			if (node.tagName === 'TEXTAREA') return true;
			if (node.tagName !== 'INPUT') return false;
			const type = (node as HTMLInputElement).type;
			return (
				type === 'text' ||
				type === 'search' ||
				type === 'email' ||
				type === 'tel' ||
				type === 'number' ||
				type === 'url' ||
				type === 'password'
			);
		};
		const update = () => {
			const viewport = window.visualViewport;
			const visible = viewport?.height ?? window.innerHeight;
			const inset = window.innerHeight - visible - (viewport?.offsetTop ?? 0);
			setKeyboardOpen(isTextEntry(document.activeElement) || inset > 80);
		};
		const onFocusOut = () => {
			window.setTimeout(update, 0);
		};
		update();
		window.visualViewport?.addEventListener('resize', update);
		window.visualViewport?.addEventListener('scroll', update);
		window.addEventListener('resize', update);
		document.addEventListener('focusin', update);
		document.addEventListener('focusout', onFocusOut);
		return () => {
			window.visualViewport?.removeEventListener('resize', update);
			window.visualViewport?.removeEventListener('scroll', update);
			window.removeEventListener('resize', update);
			document.removeEventListener('focusin', update);
			document.removeEventListener('focusout', onFocusOut);
		};
	}, []);

	const {
		fields,
		setFieldValue,
		presetFields,
		detectionError,
		canvasKey,
		focusedFieldId,
		setFocusedFieldId,
		setSignatureFieldId,
		signatureFieldId,
		handleSignatureApply,
		visibleMismatches,
		mismatchedFieldIds,
		settingsByFieldId,
		handleFieldsDetected,
		handleDetectionError,
		retryDetection,
		handleApplySuggestion,
		handleConfirm,
		submitDocument,
		zeroFieldsNotice,
		confirmEnabled,
		isSubmitting,
		mismatchVisible,
		showDecline,
		setShowDecline,
		showSubmitAnyway,
		setShowSubmitAnyway,
		suggestionFocusId,
		suggestionAllowed,
	} = session;

	const { orderedFields, completedFieldCount } =
		useClientSignOrderedFields(session);

	React.useEffect(() => {
		if (!htmlAvailable) setViewer('pdf');
	}, [htmlAvailable]);

	React.useEffect(() => {
		if (viewer === 'pdf') setPdfViewerActivated(true);
	}, [viewer]);

	React.useEffect(() => {
		const node = viewerSlotRef.current;
		if (!node) return undefined;
		const update = () => {
			setContentWidth(Math.max(1, Math.floor(node.clientWidth)));
		};
		update();
		const observer = new ResizeObserver(update);
		observer.observe(node);
		return () => observer.disconnect();
	}, [viewer, signatureFieldId]);

	const focusActiveField = (fieldId: string) => {
		if (viewer === 'html') htmlRef.current?.focusField(fieldId);
		else canvasRef.current?.focusField(fieldId);
	};

	const scrollToField = (fieldId: string) => {
		focusActiveField(fieldId);
	};

	const cycleField = (direction: -1 | 1) => {
		if (orderedFields.length === 0) return;
		const currentId = cycleFieldIdRef.current ?? focusedFieldId;
		const selectedIndex = currentId
			? orderedFields.findIndex((field) => field.id === currentId)
			: -1;
		const current =
			selectedIndex === -1 ? (direction === 1 ? -1 : 0) : selectedIndex;
		const nextIndex =
			(current + direction + orderedFields.length) % orderedFields.length;
		const nextId = orderedFields[nextIndex].id;
		cycleFieldIdRef.current = nextId;
		setFocusedFieldId(nextId);
		focusActiveField(nextId);
	};

	if (submitted) {
		return (
			<div className="flex h-full w-full min-w-0 flex-col items-center justify-center bg-white px-6 text-center">
				<Icon
					src={Icons.checkCircleEmptyState}
					width={160}
					height={160}
					className="size-[160px]"
				/>
				<Typography type="semibold-20" className="mt-4">
					Document Submitted
				</Typography>
				<DocumentSubmittedSuccessMessage
					documentName={documentName}
					className="mt-2"
				/>
				<Button
					type="button"
					decoration="primary"
					size="full"
					label="Done"
					className="mt-6"
					onClick={onClose}
				/>
			</div>
		);
	}

	if (signatureFieldId) {
		return (
			<MobileSignatureScreen
				onClose={() => setSignatureFieldId(null)}
				onApply={handleSignatureApply}
			/>
		);
	}

	const mismatchIds = mismatchVisible ? mismatchedFieldIds : [];

	return (
		<div className="relative flex flex-col gap-3 h-full min-h-0 bg-[#F4F0FA]">
			<div className="flex gap-x-2 items-center justify-between bg-white pb-2 px-4">
				<ClientSignFieldProgressNav
					className="min-w-0 flex-1"
					orientation="vertical"
					completed={completedFieldCount}
					total={orderedFields.length}
					onPrevious={() => cycleField(-1)}
					onNext={() => cycleField(1)}
				/>
				<div className="flex justify-end shrink-0">
					<button
						type="button"
						disabled={!htmlAvailable}
						onClick={() =>
							setViewer((current) => (current === 'pdf' ? 'html' : 'pdf'))
						}
						className="rounded-full border border-purple-150 px-3 py-1.5 text-[13px] font-semibold text-purple-150 disabled:cursor-not-allowed disabled:border-[#0D061614] disabled:text-[#6E6A73]"
					>
						{htmlAvailable
							? viewer === 'pdf'
								? 'Mobile view'
								: 'PDF view'
							: 'Mobile view unavailable'}
					</button>
				</div>
			</div>

			<div
				ref={viewerSlotRef}
				className="flex-1 min-h-0 overflow-y-auto scroll-custom rounded-lg mx-3"
			>
				{mismatchIds.length > 0 && (
					<ClientSignMobileMismatch
						fieldIds={mismatchIds}
						fields={fields}
						settingsByFieldId={settingsByFieldId}
					/>
				)}
				{detectionError && viewer === 'pdf' && (
					<div className="flex flex-col items-center justify-center gap-3 py-16 px-4">
						<Typography type="regular-14" className="text-main-red text-center">
							We couldn’t read the fields in this document. Please try again.
						</Typography>
						<Button
							type="button"
							decoration="primary"
							size="fit"
							label="Retry"
							onClick={retryDetection}
						/>
					</div>
				)}
				{pdfViewerActivated && documentUrl && !detectionError && (
					<div
						className={clsx(viewer !== 'pdf' && 'hidden')}
						aria-hidden={viewer !== 'pdf'}
					>
						<ClientSignPdfCanvas
							key={canvasKey}
							ref={canvasRef}
							pdfUrl={documentUrl}
							fields={fields}
							onFieldValueChange={setFieldValue}
							onSignatureClick={setSignatureFieldId}
							presetFields={presetFields}
							onFieldsDetected={handleFieldsDetected}
							onDetectionError={handleDetectionError}
							zoomScale={1}
							baseContentWidth={contentWidth}
							minOverlayHeight={10}
							mismatchedFieldIds={visibleMismatches}
							focusedFieldId={suggestionAllowed ? suggestionFocusId : null}
							settingsByFieldId={settingsByFieldId}
							onApplySuggestion={handleApplySuggestion}
							onFieldFocus={(fieldId) => {
								cycleFieldIdRef.current = fieldId;
								setFocusedFieldId(fieldId);
							}}
							onFieldBlur={(fieldId) =>
								setFocusedFieldId((current) =>
									current === fieldId ? null : current
								)
							}
						/>
					</div>
				)}
				{htmlAvailable && (
					<div
						className={clsx(viewer !== 'html' && 'hidden')}
						aria-hidden={viewer !== 'html'}
					>
						<ClientSignHtmlView
							ref={htmlRef}
							pages={textPages}
							images={documentImages}
							formFields={extractedFields ?? []}
							fields={fields}
							onFieldValueChange={setFieldValue}
							onSignatureClick={setSignatureFieldId}
							settingsByFieldId={settingsByFieldId}
							suggestionFieldId={suggestionAllowed ? suggestionFocusId : null}
							onApplySuggestion={handleApplySuggestion}
							onFieldFocus={(fieldId) => {
								cycleFieldIdRef.current = fieldId;
								setFocusedFieldId(fieldId);
							}}
							onFieldBlur={(fieldId) =>
								setFocusedFieldId((current) =>
									current === fieldId ? null : current
								)
							}
						/>
					</div>
				)}
			</div>

			{zeroFieldsNotice && (
				<div className="mt-3 rounded-lg bg-grey-5 px-4 py-3">
					<Typography type="regular-14">
						No fillable fields were found. You can still confirm and submit this
						document.
					</Typography>
				</div>
			)}

			<div
				className={clsx(
					'flex flex-col gap-3 shrink-0 bg-white p-4',
					keyboardOpen && 'hidden'
				)}
			>
				<Button
					type="button"
					decoration={mismatchVisible ? 'secondary' : 'primary'}
					size="full"
					label={
						isSubmitting
							? 'Submitting…'
							: mismatchVisible
							? 'Submit Anyway'
							: 'Confirm & Submit'
					}
					disabled={mismatchVisible ? isSubmitting : !confirmEnabled}
					loading={isSubmitting}
					className={clsx(
						'disabled:!bg-[#0D06160F] disabled:!text-[#0D06164D] disabled:!border-none',
						{
							'pointer-events-none': preview,
						}
					)}
					onClick={() =>
						mismatchVisible
							? setShowSubmitAnyway(true)
							: handleConfirm(scrollToField)
					}
				/>
				{!wasPreviouslyDeclined && (
					<Button
						type="button"
						decoration="none"
						size="full"
						className="border-[#E4E4E4] bg-white text-[#7E7E7E]"
						label="Decline Document"
						disabled={isSubmitting}
						onClick={() => setShowDecline(true)}
					/>
				)}
			</div>
			{showDecline && (
				<div className="absolute inset-x-0 bottom-0 z-20 rounded-t-3xl border-t border-[#0D061614] bg-white p-5">
					<Typography type="semibold-16" className="!text-left">
						Are you sure you want to decline?
					</Typography>
					<Typography
						type="regular-14"
						className="mt-3 text-gray-35 !text-left"
					>
						Declining to sign this document may affect your ability to
						participate in this case.
					</Typography>
					<div className="mt-4 flex flex-col gap-3">
						<Button
							type="button"
							decoration="danger-outline"
							size="full"
							label="Confirm"
							loading={isSubmitting}
							disabled={isSubmitting || preview}
							onClick={() => void submitDocument(false)}
						/>
						<Button
							type="button"
							decoration="secondary"
							size="full"
							label="Cancel"
							disabled={isSubmitting}
							onClick={() => setShowDecline(false)}
						/>
					</div>
				</div>
			)}
			{showSubmitAnyway && (
				<div className="absolute inset-x-0 bottom-0 z-20 rounded-t-3xl border-t border-[#0D061614] bg-white p-5">
					<Typography type="semibold-16" className="!text-left">
						Note:
					</Typography>
					<Typography
						type="regular-14"
						className="mt-3 text-gray-35 !text-left"
					>
						It is very likely that a lawyer will contact you to verify this
						information.
					</Typography>
					<div className="mt-4 flex flex-col gap-3">
						<Button
							type="button"
							decoration="primary"
							size="full"
							label="Submit"
							loading={isSubmitting}
							disabled={isSubmitting || preview}
							onClick={() => void submitDocument(true)}
						/>
						<Button
							type="button"
							decoration="secondary"
							size="full"
							label="Back"
							disabled={isSubmitting}
							onClick={() => setShowSubmitAnyway(false)}
						/>
					</div>
				</div>
			)}
		</div>
	);
};

export default ClientSignMobileView;
